import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'

const COLOR_OPTIONS = ['#F5F9FF', '#F4CF62', '#7DB5FF', '#6EE4C0', '#FF9090']
const FONT_OPTIONS = [
  { label: 'Sans', value: 'var(--ff-u)' },
  { label: 'Serif', value: 'var(--ff-d)' },
  { label: 'Mono', value: 'var(--ff-m)' },
]

const storageName = (storageKey) => `resultos-winning-result-rich-${storageKey}`

function escapeHtml(text = '') {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
    .replace(/\n/g, '<br>')
}

function getStoredHtml(storageKey) {
  if (typeof window === 'undefined' || !storageKey) return ''
  return window.localStorage.getItem(storageName(storageKey)) || ''
}

export function getWinningResultRichHtml(storageKey, fallbackText = '') {
  return getStoredHtml(storageKey) || escapeHtml(fallbackText)
}

function getPlainText(element) {
  return (element?.innerText || '').replace(/\n\n+/g, '\n').trim()
}

function selectionBelongsToEditor(editor) {
  const selection = window.getSelection()
  if (!selection || selection.rangeCount === 0) return false
  const node = selection.getRangeAt(0).commonAncestorContainer
  return editor.contains(node.nodeType === Node.TEXT_NODE ? node.parentNode : node)
}

const RichWinningResultEditor = forwardRef(function RichWinningResultEditor({
  autoFocus = false,
  minHeight = 120,
  onChange,
  onEnterCommit,
  placeholder,
  storageKey,
  value,
}, ref) {
  const editorRef = useRef(null)
  const savedRangeRef = useRef(null)
  const [fontSize, setFontSize] = useState(18)

  useImperativeHandle(ref, () => ({
    focus: () => editorRef.current?.focus(),
  }))

  useEffect(() => {
    const editor = editorRef.current
    if (!editor) return

    const storedHtml = getStoredHtml(storageKey)
    editor.innerHTML = storedHtml || escapeHtml(value)
  }, [storageKey])

  useEffect(() => {
    const editor = editorRef.current
    if (!editor || document.activeElement === editor) return

    const plain = getPlainText(editor)
    if (plain !== (value || '').trim()) {
      editor.innerHTML = escapeHtml(value)
    }
  }, [value])

  useEffect(() => {
    if (autoFocus) {
      setTimeout(() => editorRef.current?.focus(), 60)
    }
  }, [autoFocus])

  const syncValue = () => {
    const editor = editorRef.current
    if (!editor) return

    if (typeof window !== 'undefined' && storageKey) {
      window.localStorage.setItem(storageName(storageKey), editor.innerHTML)
    }

    onChange(getPlainText(editor))
  }

  const focusEditor = () => {
    editorRef.current?.focus()
  }

  const rememberSelection = () => {
    const editor = editorRef.current
    if (!editor || !selectionBelongsToEditor(editor)) return
    savedRangeRef.current = window.getSelection().getRangeAt(0).cloneRange()
  }

  const restoreSelection = () => {
    const range = savedRangeRef.current
    if (!range) return
    const selection = window.getSelection()
    selection.removeAllRanges()
    selection.addRange(range)
  }

  const applyCommand = (command, commandValue = null) => {
    focusEditor()
    restoreSelection()
    document.execCommand(command, false, commandValue)
    syncValue()
  }

  const applyInlineStyle = (style) => {
    const editor = editorRef.current
    focusEditor()
    restoreSelection()
    if (!editor || !selectionBelongsToEditor(editor)) {
      return
    }

    const selection = window.getSelection()
    const range = selection.getRangeAt(0)
    if (range.collapsed) {
      focusEditor()
      return
    }

    const span = document.createElement('span')
    Object.assign(span.style, style)
    span.appendChild(range.extractContents())
    range.insertNode(span)

    selection.removeAllRanges()
    const nextRange = document.createRange()
    nextRange.selectNodeContents(span)
    selection.addRange(nextRange)
    savedRangeRef.current = nextRange.cloneRange()

    syncValue()
  }

  const handlePaste = (event) => {
    event.preventDefault()
    const text = event.clipboardData.getData('text/plain')
    document.execCommand('insertText', false, text)
    syncValue()
  }

  return (
    <div className="wr-editor-wrap">
      <div className="wr-style-toolbar" aria-label="Winning Result selected text formatting">
        <div className="wr-style-group">
          <select
            className="wr-style-select"
            defaultValue=""
            onMouseDown={(event) => event.stopPropagation()}
            onChange={(event) => {
              if (event.target.value) applyInlineStyle({ fontFamily: event.target.value })
              event.target.value = ''
            }}
            aria-label="Apply font to selected text"
          >
            <option value="" disabled>Font</option>
            {FONT_OPTIONS.map((font) => (
              <option key={font.value} value={font.value}>{font.label}</option>
            ))}
          </select>
          <label className="wr-size-control">
            <span>Size</span>
            <input
              type="number"
              min="12"
              max="40"
              value={fontSize}
              onChange={(event) => setFontSize(Number(event.target.value) || 18)}
              aria-label="Font size"
            />
          </label>
          <button type="button" className="wr-reset-btn" onMouseDown={(event) => event.preventDefault()} onClick={() => applyInlineStyle({ fontSize: `${fontSize}px` })}>
            Apply
          </button>
        </div>
        <div className="wr-style-group">
          <button type="button" className="wr-format-btn" onMouseDown={(event) => event.preventDefault()} onClick={() => applyCommand('bold')} title="Bold">B</button>
          <button type="button" className="wr-format-btn italic" onMouseDown={(event) => event.preventDefault()} onClick={() => applyCommand('italic')} title="Italic">I</button>
          <button type="button" className="wr-format-btn underline" onMouseDown={(event) => event.preventDefault()} onClick={() => applyCommand('underline')} title="Underline">U</button>
        </div>
        <div className="wr-style-group">
          <div className="wr-color-swatches" aria-label="Apply font color to selected text">
            {COLOR_OPTIONS.map((color) => (
              <button
                key={color}
                type="button"
                className="wr-color-btn"
                style={{ backgroundColor: color }}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => applyCommand('foreColor', color)}
                aria-label={`Use color ${color}`}
              />
            ))}
          </div>
          <label className="wr-color-picker" title="Custom font color">
            <input
              type="color"
              defaultValue="#F5F9FF"
              onChange={(event) => applyCommand('foreColor', event.target.value)}
              aria-label="Custom font color"
            />
          </label>
          <button type="button" className="wr-reset-btn" onMouseDown={(event) => event.preventDefault()} onClick={() => applyCommand('removeFormat')}>Clear</button>
        </div>
      </div>
      <div
        ref={editorRef}
        className="wr-rich-editor"
        contentEditable
        data-placeholder={placeholder}
        role="textbox"
        aria-multiline="true"
        style={{ minHeight }}
        onInput={syncValue}
        onKeyUp={rememberSelection}
        onMouseUp={rememberSelection}
        onPaste={handlePaste}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && !event.shiftKey && onEnterCommit) {
            event.preventDefault()
            onEnterCommit()
          }
        }}
        suppressContentEditableWarning
      />
    </div>
  )
})

export default RichWinningResultEditor
